package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.layout.engine.LayoutResult
import top.wcpe.allincore.ui.runtime.element.UIElement

/**
 * 焦点管理器。
 *
 * 持有当前获得焦点的元素引用，并同步 [UIElement.focused] 视觉状态与
 * [EventTarget.onFocusGainedHandler] / [EventTarget.onFocusLostHandler] 回调。
 *
 * 设计为非单例：客户端 UI 运行时持有者（如 Screen）自行持有一个实例。
 */
class FocusManager {

    private var currentFocus: UIElement<*>? = null

    /**
     * 把焦点切换到指定元素。null 等价于 [clear]。会将旧元素 focused=false 并回调其 onFocusLost，
     * 再将新元素 focused=true 并回调其 onFocusGained。
     */
    fun focus(element: UIElement<*>?) {
        if (currentFocus === element) {
            return
        }
        val old = currentFocus
        if (old != null) {
            old.focused(false)
            val lost = old.onFocusLostHandler()
            if (lost != null) {
                InputDispatcher.invokeHandler(lost, FocusEvent(false), "onFocusLost", old)
            }
        }
        currentFocus = element
        if (element != null) {
            element.focused(true)
            val gained = element.onFocusGainedHandler()
            if (gained != null) {
                InputDispatcher.invokeHandler(gained, FocusEvent(true), "onFocusGained", element)
            }
        }
    }

    /** 清空当前焦点。 */
    fun clear() {
        focus(null)
    }

    /** 取得当前焦点元素，可能为 null。 */
    fun current(): UIElement<*>? = currentFocus

    // -------- 兼容既有 API --------

    @Deprecated("使用 focus(element)", ReplaceWith("focus(element)"))
    fun requestFocus(element: UIElement<*>?) {
        focus(element)
    }

    @Deprecated("使用 clear()", ReplaceWith("clear()"))
    fun clearFocus() {
        clear()
    }

    // -------- Tab 循环 --------

    /** Tab 切换：DFS 找到下一个 focusable 元素并 focus；无候选则保持现状。 */
    fun cycleNext(root: UIElement<*>?) {
        cycle(root, true)
    }

    /** Shift+Tab 切换：DFS 找到上一个 focusable 元素并 focus。 */
    fun cyclePrev(root: UIElement<*>?) {
        cycle(root, false)
    }

    private fun cycle(root: UIElement<*>?, forward: Boolean) {
        if (root == null) {
            return
        }
        // 阶段 13：先按 DOM 顺序收集所有 focusable 元素，再做稳定排序：
        // - 显式 tabIndex 升序在前；
        // - 未声明 tabIndex（TAB_INDEX_UNSET）按原 DOM 索引兜底；
        // - 同 tabIndex 元素按 DOM 顺序兜底，保证可预测。
        val collected = ArrayList<IndexedFocusable>()
        collectFocusable(root, collected)
        if (collected.isEmpty()) {
            return
        }
        collected.sortWith(::compareFocusOrder)
        val focusables = collected.map { it.element }
        val idx = focusables.indexOf(currentFocus)
        val next = when {
            idx < 0 -> if (forward) 0 else focusables.size - 1
            forward -> (idx + 1) % focusables.size
            else -> (idx - 1 + focusables.size) % focusables.size
        }
        focus(focusables[next])
    }

    /** Tab 顺序比较：显式 tabIndex 优先且升序，其余按 DOM 顺序兜底。 */
    private fun compareFocusOrder(a: IndexedFocusable, b: IndexedFocusable): Int {
        val aIdx = a.element.tabIndex()
        val bIdx = b.element.tabIndex()
        val aHas = aIdx != UIElement.TAB_INDEX_UNSET
        val bHas = bIdx != UIElement.TAB_INDEX_UNSET
        return when {
            aHas != bHas -> if (aHas) -1 else 1
            aHas && aIdx != bIdx -> aIdx.compareTo(bIdx)
            else -> a.domOrder.compareTo(b.domOrder)
        }
    }

    private fun collectFocusable(node: UIElement<*>, out: MutableList<IndexedFocusable>) {
        if (node.isFocusable()) {
            out.add(IndexedFocusable(node, out.size))
        }
        for (child in node.children()) {
            collectFocusable(child, out)
        }
    }

    /** 阶段 13：收集 focusable 元素时附带 DOM 顺序索引，保证 tabIndex 排序后兜底稳定。 */
    private data class IndexedFocusable(val element: UIElement<*>, val domOrder: Int)

    // -------- 点击联动焦点 --------

    /**
     * 点击时根据命中情况设置焦点：复用 [HitTester] 的命中链——沿「根 → 最深命中」链从末端向根取首个
     * focusable 元素 focus；整链无 focusable 或未命中则 [clear]。
     *
     * 只修改焦点状态，不负责分发 click handler（调用方自行再调 [InputDispatcher.dispatchClick]）。
     */
    fun dispatchClickFocus(root: UIElement<*>?, layout: LayoutResult?, mx: Double, my: Double) {
        if (root == null || layout == null) {
            clear()
            return
        }
        val path = ArrayList<UIElement<*>>()
        if (!HitTester.hit(root, layout, mx, my, path, null)) {
            clear()
            return
        }
        // 命中链从 root 写到最深命中元素；从末端向根取首个 focusable，即「最深的 focusable 命中元素」。
        for (i in path.indices.reversed()) {
            val candidate = path[i]
            if (candidate.isFocusable()) {
                focus(candidate)
                return
            }
        }
        clear()
    }
}
